import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, StatCard, StatusPill } from "../components/shared/Primitives";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { downloadCSV } from "../lib/csv";

const SOURCE_DOT = {
  "Daily To-Do": "var(--rw-dot-todo)",
  "Pipeline Report": "var(--rw-dot-pipeline)",
  "Calling Activity": "var(--rw-dot-calling)",
  "YTD Calling Activity": "var(--rw-dot-calling)",
  "Professional Introductions": "var(--rw-dot-intros)",
  "Call Schedule": "var(--rw-dot-schedule)",
  "Monthly Call Schedule": "var(--rw-dot-schedule)",
  "Networking": "#8E4EC6",
};

export default function FollowUps() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/follow-ups");
    setItems(data);
  };
  useEffect(() => {
    load();
  }, []);

  const today = new Date().toISOString().slice(0, 10);

  const filtered = useMemo(() => {
    let arr = items;
    if (filter === "overdue") arr = arr.filter((i) => i.follow_up_date && i.follow_up_date < today && !i.follow_up_completed);
    else if (filter === "today") arr = arr.filter((i) => i.follow_up_date === today && !i.follow_up_completed);
    else if (filter === "upcoming") arr = arr.filter((i) => i.follow_up_date && i.follow_up_date > today && !i.follow_up_completed);
    else if (filter === "completed") arr = arr.filter((i) => i.follow_up_completed);
    else if (filter === "priority") arr = arr.filter((i) => (i.priority || "").toLowerCase() === "priority");
    if (q) {
      const ql = q.toLowerCase();
      arr = arr.filter(
        (i) =>
          (i.follow_up || "").toLowerCase().includes(ql) ||
          (i.individual || "").toLowerCase().includes(ql) ||
          (i.company || "").toLowerCase().includes(ql) ||
          (i.notes || "").toLowerCase().includes(ql)
      );
    }
    return arr;
  }, [items, filter, q, today]);

  const stats = {
    overdue: items.filter((i) => i.follow_up_date && i.follow_up_date < today && !i.follow_up_completed).length,
    today: items.filter((i) => i.follow_up_date === today && !i.follow_up_completed).length,
    upcoming: items.filter((i) => i.follow_up_date && i.follow_up_date > today && !i.follow_up_completed).length,
    completed: items.filter((i) => i.follow_up_completed).length,
  };

  const complete = async (i) => {
    await api.post(`/follow-ups/${i.kind}/${i.entry_id}/complete`);
    toast.success("Marked complete");
    load();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Action items"
        title="Follow-Ups"
        description="A central command center for every follow-up across your operations."
        right={
          <Button
            variant="outline"
            onClick={() =>
              downloadCSV("follow-ups.csv", filtered, [
                { key: "source", label: "Source" },
                { key: "individual", label: "Individual" },
                { key: "company", label: "Company" },
                { key: "follow_up", label: "Follow-up" },
                { key: "follow_up_date", label: "Due" },
                { key: "status", label: "Status" },
                { key: "priority", label: "Priority" },
                { key: "notes", label: "Notes" },
              ])
            }
            data-testid="fu-export-csv"
          >
            <Download size={14} className="mr-2" /> Export CSV
          </Button>
        }
      />
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Overdue" value={stats.overdue} testid="fu-overdue" />
          <StatCard label="Due Today" value={stats.today} testid="fu-today" />
          <StatCard label="Upcoming" value={stats.upcoming} testid="fu-upcoming" />
          <StatCard label="Completed" value={stats.completed} testid="fu-completed" />
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 flex-wrap">
            {["all", "overdue", "today", "upcoming", "priority", "completed"].map((f) => (
              <Button
                key={f}
                size="sm"
                variant={filter === f ? "default" : "outline"}
                className={`capitalize ${filter === f ? "bg-[var(--rw-sidebar)] hover:bg-[var(--rw-sidebar-hover)]" : ""}`}
                onClick={() => setFilter(f)}
                data-testid={`fu-filter-${f}`}
              >
                {f}
              </Button>
            ))}
          </div>
          <Input
            placeholder="Search follow-ups…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-xs"
            data-testid="fu-search"
          />
        </div>

        <div className="bg-white border border-[var(--rw-border)] rounded-[4px] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--rw-surface-2)]">
              <tr className="text-left text-[10px] uppercase tracking-[0.08em] font-semibold text-neutral-500">
                <th className="py-2 px-3"></th>
                <th className="py-2 px-3">Source</th>
                <th className="py-2 px-3">Contact / Company</th>
                <th className="py-2 px-3">Follow-up</th>
                <th className="py-2 px-3">Due</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-neutral-400">No follow-ups match.</td></tr>
              )}
              {filtered.map((i, idx) => {
                const overdue = i.follow_up_date && i.follow_up_date < today && !i.follow_up_completed;
                return (
                  <tr key={idx} className={`border-t border-[var(--rw-border)] hover:bg-[var(--rw-surface-2)] ${i.follow_up_completed ? "row-completed" : ""} ${overdue ? "row-priority" : ""}`} data-testid={`fu-row-${idx}`}>
                    <td className="py-2 px-3">
                      <input
                        type="checkbox"
                        checked={!!i.follow_up_completed}
                        onChange={() => !i.follow_up_completed && complete(i)}
                        data-testid={`fu-complete-${idx}`}
                      />
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <span className="nav-dot" style={{ background: SOURCE_DOT[i.source] || "#999" }} />
                        <span className="text-xs">{i.source}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="font-semibold">{i.individual || "—"}</div>
                      <div className="text-xs text-neutral-500">{i.company}</div>
                    </td>
                    <td className="py-2 px-3 max-w-md">
                      <div className="text-sm">{i.follow_up}</div>
                      {i.notes && <div className="text-xs text-neutral-500 line-clamp-1 mt-0.5">{i.notes}</div>}
                    </td>
                    <td className="py-2 px-3 font-mono-rw text-xs">{i.follow_up_date || "—"}</td>
                    <td className="py-2 px-3"><StatusPill value={i.status} /></td>
                    <td className="py-2 px-3 text-right">
                      {!i.follow_up_completed && (
                        <Button size="sm" variant="outline" onClick={() => complete(i)} data-testid={`fu-done-${idx}`}>
                          Done
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
